import { Card, Spinner } from "react-bootstrap";
import {
  MessageSquare,
  CheckCircle,
  ArrowRight,
  MousePointerClick,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "helpers/api_url";
import { WarehouseOption } from "./DashboardFilters";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import DashboardTableContainer, {
  CustomColumnDef,
} from "Common/Tables/ObjectFiltersTable/DashboardTableContainer";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import { fetchEnquirySummary, fetchEnquiryList } from "slices/Dashboard/Enquiry_Summary/thunk";
import { DashboardSlidePanel } from "./Reusable/DashboardSlidePanel";
import "./EnquirySummary.css";

interface EnquirySummaryProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

interface EnquirySummaryData {
  total_enquiries: number;
  with_site_visit: number;
  without_site_visit: number;
}

interface EnquiryListingItem {
  enquiry_id: string;
  enquiry_number: string;
  enquiry_date: string;
  customer_name: string;
  contact_person_name: string;
  contact_person_phone_no: string;
  enquiry_source: string | null;
  stage: string;
  site_visit_required: boolean;
  has_site_visit: boolean;
  site_visit_date: string | null;
  remark: string | null;
  created_at: string;
  updated_at: string;
  created_by_username: string | null;
  updated_by_username: string | null;
}

export function EnquirySummary({
  dateFrom,
  dateTo,
  warehouse = [],
}: EnquirySummaryProps) {
  const dispatch = useDispatch<any>();
  const { summary: enquirySummaryData, summaryLoading: loading, enquiryList, totalRecords, listLoading } = useSelector((state: any) => state.EnquirySummaryReducer);
  
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "withSiteVisit" | "withoutSiteVisit"
  >("all");

  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Fetch enquiry summary data
  useEffect(() => {
    dispatch(fetchEnquirySummary({ dateFrom, dateTo, warehouse })).catch((error: any) => {
      console.error("Error fetching enquiry summary data:", error);
      toast.error("Failed to load enquiry summary data");
    });
  }, [dispatch, dateFrom, dateTo, warehouse]);

  const totalEnquiries = enquirySummaryData?.total_enquiries ?? 0;
  const withSiteVisit = enquirySummaryData?.with_site_visit ?? 0;
  const withoutSiteVisit = enquirySummaryData?.without_site_visit ?? 0;

  const handleCardClick = (
    filter: "all" | "withSiteVisit" | "withoutSiteVisit"
  ) => {
    setSelectedFilter(filter);
    setIsPanelOpen(true);
    setPageIndex(0); // Reset to first page when opening panel
  };

  // Convert selectedFilter to API filter_type
  const filterType = useMemo(():
    | "all"
    | "with_site_visit"
    | "without_site_visit" => {
    if (selectedFilter === "withSiteVisit") return "with_site_visit";
    if (selectedFilter === "withoutSiteVisit") return "without_site_visit";
    return "all";
  }, [selectedFilter]);

  // Fetch enquiry list data
  useEffect(() => {
    if (!isPanelOpen) return;

    dispatch(fetchEnquiryList({
      page: pageIndex + 1,
      pageSize,
      filterType,
      dateFrom,
      dateTo,
      warehouse,
      sorting,
      filters: filterValue,
    })).catch((error: any) => {
      console.error("Error fetching enquiry list:", error);
      toast.error("Failed to load enquiry list");
    });
  }, [
    dispatch,
    isPanelOpen,
    pageIndex,
    pageSize,
    sorting,
    filterValue,
    filterType,
    dateFrom,
    dateTo,
    warehouse,
  ]);

  // Reset page when filters change
  useEffect(() => {
    if (isPanelOpen) {
      setPageIndex(0);
    }
  }, [
    sorting,
    pageSize,
    columnFilters,
    filterValue,
    selectedFilter,
    dateFrom,
    dateTo,
    warehouse,
    isPanelOpen,
  ]);

  const getEnquiryStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; label: string }> = {
      Pending: {
        className: "enquiry-summary-badge enquiry-summary-badge-new",
        label: "Pending",
      },
      "Request for Quotation": {
        className: "enquiry-summary-badge enquiry-summary-badge-scheduled",
        label: "Request for Quotation",
      },
      Quoted: {
        className: "enquiry-summary-badge enquiry-summary-badge-quoted",
        label: "Quoted",
      },
      Closed: {
        className: "enquiry-summary-badge enquiry-summary-badge-closed",
        label: "Closed",
      },
    };
    const config = statusConfig[status] || {
      className: "enquiry-summary-badge enquiry-summary-badge-new",
      label: status || "Pending",
    };
    return <span className={config.className}>{config.label}</span>;
  };

  const getPanelTitle = () => {
    if (selectedFilter === "withSiteVisit") return "Enquiries With Site Visit";
    if (selectedFilter === "withoutSiteVisit")
      return "Enquiries Without Site Visit";
    return "All Enquiries";
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Define columns for the table
  const columns = useMemo<CustomColumnDef<EnquiryListingItem>[]>(
    () => {
      // Helper function to build dropdown URL with filters
      const buildDropdownUrl = (fieldName: string) => {
        const params = new URLSearchParams();
        if (dateFrom) {
          params.append('date_from', dateFrom.toISOString().split('T')[0]);
        }
        if (dateTo) {
          params.append('date_to', dateTo.toISOString().split('T')[0]);
        }
        if (warehouse.length > 0) {
          params.append('warehouse_id', warehouse.map(w => w.value).join(','));
        }
        params.append('filter_type', filterType);
        const queryString = params.toString();
        return `${api?.dashboad?.enquiry_dropdown}/${fieldName}${queryString ? `?${queryString}` : ''}`;
      };

      return [
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("enquiry_number"),
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {row.original.enquiry_number || "-"}
            </span>
          );
        },
      },
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {formatDate(row.original.enquiry_date)}
            </span>
          );
        },
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("customer_name"),
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {row.original.customer_details?.customer_name || "-"}
            </span>
          );
        },
      },
      {
        header: "Contact Person",
        accessorKey: "contact_person_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("contact_person_name"),
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {row.original.customer_details?.contact_person_name || "-"}
            </span>
          );
        },
      },
      {
        header: "Phone",
        accessorKey: "contact_person_phone_no",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("contact_person_phone_no"),
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {row.original.customer_details?.contact_person_phone_no || "-"}
            </span>
          );
        },
      },
      {
        header: "Enquiry Source",
        accessorKey: "enquiry_source",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("enquiry_source"),
        cell: ({ row }: { row: any }) => {
          return (
            <span className="enquiry-summary-table-styles">
              {row.original.enquiry_source || "-"}
            </span>
          );
        },
      },
      {
        header: "Stage",
        accessorKey: "stage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: buildDropdownUrl("stage"),
        cell: ({ row }: { row: any }) => {
          return getEnquiryStatusBadge(row.original.stage);
        },
      },
      {
        header: "Site Visit",
        accessorKey: "has_site_visit",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const enquiry = row.original;
          if (enquiry?.site_visit_required && enquiry?.site_visit?.site_visit_date) {
            return (
              <div className="enquiry-summary-site-visit-container">
                <span className="enquiry-summary-badge enquiry-summary-badge-site-visit-yes">
                  <CheckCircle className="enquiry-summary-badge-icon" />
                  Yes
                </span>
                <span className="enquiry-summary-table-styles">
                  {formatDate(enquiry.site_visit?.site_visit_date)}
                </span>
              </div>
            );
          }
          return (
            <span className="enquiry-summary-badge enquiry-summary-badge-site-visit-no">
              No
            </span>
          );
        },
      },
      {
        header: "Remark",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            <span
              className="enquiry-summary-table-styles"
              title={row.original.remark || ""}
            >
              {row.original.remark || "-"}
            </span>
          );
        },
      },
    ];
    },
    [dateFrom, dateTo, warehouse, filterType]
  );

  return (
    <Card className="enquiry-summary-main-card shadow-sm border-0 w-100 h-100">
      <Card.Body className="p-4">
        {/* Header */}
        <div className="mb-3 pb-2 border-bottom position-relative">
          <div className="order-summary-title-container">
            <MessageSquare className="order-summary-icon" />
            <h3 className="order-summary-title">Enquiry Summary</h3>
          </div>
        </div>

        {/* Clickable Summary Cards */}
        <div className="enquiry-summary-cards-grid">
          <div
            onClick={() => !loading && handleCardClick("all")}
            className={`enquiry-summary-card enquiry-summary-card-purple ${
              loading ? "enquiry-summary-card-loading" : ""
            }`}
          >
            <div className="enquiry-summary-card-arrow">
              <ArrowRight
                className="enquiry-summary-icon"
                style={{ color: "#9333ea" }}
              />
            </div>
            <p className="enquiry-summary-card-label enquiry-summary-card-label-purple">
              Total Enquiries
            </p>
            {loading ? (
              <div
                className="d-flex align-items-center justify-content-center enquiry-summary-card-value"
                style={{ minHeight: "2.5rem" }}
              >
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            ) : (
              <p className="enquiry-summary-card-value">{totalEnquiries}</p>
            )}

            <div className="enquiry-summary-card-hint enquiry-summary-card-label-purple">
              <MousePointerClick className="enquiry-summary-card-hint-icon" />
              <span>Click to view all</span>
            </div>
          </div>

          <div
            onClick={() => !loading && handleCardClick("withSiteVisit")}
            className={`enquiry-summary-card enquiry-summary-card-green ${
              loading ? "enquiry-summary-card-loading" : ""
            }`}
          >
            <div className="enquiry-summary-card-arrow">
              <ArrowRight
                className="enquiry-summary-icon"
                style={{ color: "#16a34a" }}
              />
            </div>
            <p className="enquiry-summary-card-label enquiry-summary-card-label-green">
              With Site Visit
            </p>
            {loading ? (
              <div
                className="d-flex align-items-center justify-content-center enquiry-summary-card-value"
                style={{ minHeight: "2.5rem" }}
              >
                <Spinner animation="border" variant="success" size="sm" />
              </div>
            ) : (
              <p className="enquiry-summary-card-value">{withSiteVisit}</p>
            )}

            <div className="enquiry-summary-card-hint enquiry-summary-card-label-green">
              <MousePointerClick className="enquiry-summary-card-hint-icon" />
              <span>Click to view all</span>
            </div>
          </div>

          <div
            onClick={() => !loading && handleCardClick("withoutSiteVisit")}
            className={`enquiry-summary-card enquiry-summary-card-gray ${
              loading ? "enquiry-summary-card-loading" : ""
            }`}
          >
            <div className="enquiry-summary-card-arrow">
              <ArrowRight
                className="enquiry-summary-icon"
                style={{ color: "#6b7280" }}
              />
            </div>
            <p className="enquiry-summary-card-label enquiry-summary-card-label-gray">
              Without Site Visit
            </p>
            {loading ? (
              <div
                className="d-flex align-items-center justify-content-center enquiry-summary-card-value"
                style={{ minHeight: "2.5rem" }}
              >
                <Spinner animation="border" variant="secondary" size="sm" />
              </div>
            ) : (
              <p className="enquiry-summary-card-value">{withoutSiteVisit}</p>
            )}

            <div className="enquiry-summary-card-hint enquiry-summary-card-label-gray">
              <MousePointerClick className="enquiry-summary-card-hint-icon" />
              <span>Click to view all</span>
            </div>
          </div>
        </div>

        {/* Slide-out Panel */}
        <DashboardSlidePanel
          isOpen={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          icon={MessageSquare}
          title={getPanelTitle()}
          subtitle={`${totalRecords} enquiries found`}
          loading={listLoading}
          columns={columns}
          data={enquiryList}
          totalRecords={totalRecords}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageChange={setPageIndex}
          onPageSizeChange={setPageSize}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          colValue={filterValue}
          setFilterValue={setFilterValue}
          sorting={sorting}
          onSortingChange={setSorting}
          searchPlaceholder="Search enquiries..."
        />
      </Card.Body>
    </Card>
  );
}
